import { useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { type ColumnMappingState, RUNNING_OR_DONE_STATUSES, defaultColumn, sampleValuesFor } from './helpers';
import { FilenameStep } from './steps/filename-step';
import { LoadFileSettingsStep } from './steps/load-file-settings-step';
import { MappingStep } from './steps/mapping-step';
import { ProgressStep } from './steps/progress-step';
import { type LoadFileDelimiters } from '../../../../../../../../../backend/src/integrations/relativity/load-file-import/concordance-dat-parser';
import { type ParseHeaderSampleResponse } from '../../../../../../../../../backend/src/integrations/relativity/load-file-import/load-file-import-types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useParseLoadFileHeaderSample } from '@/api/mutations/useParseLoadFileHeaderSample';
import { type LoadFileMatchSettings, useStartLoadFileImport } from '@/api/mutations/useStartLoadFileImport';
import useGetLoadFileImport from '@/api/queries/useGetLoadFileImport';
import { Icon } from '@/new/components/icon';
import { SettingsBackLink } from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

const STEPS = [
  {
    label: 'Load file settings',
    description: 'Confirm the encoding and delimiters used to read the load file.',
  },
  {
    label: 'Filename identifier',
    description: 'Pick a column from the uploaded file that will be used to match rows to filenames in Wexler.',
  },
  {
    label: 'Field mapping',
    description:
      'Select the fields from the load file you want to import into Wexler. For each, confirm the type, and mark multi-value fields as arrays.',
  },
  { label: 'Import', description: 'Track the progress of your import.' },
] as const;

/** Shared state threaded through the wizard steps. */
export interface WizardState {
  filenameColumnHeader: string | null;
  matchSettings: LoadFileMatchSettings;
  columnMapping: ColumnMappingState[];
}

export function LoadFileImportWizard() {
  const { matterId, importId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state as {
    parseResult?: ParseHeaderSampleResponse;
    delimiters?: LoadFileDelimiters;
  } | null;

  const [parseResult, setParseResult] = useState<ParseHeaderSampleResponse | null>(navState?.parseResult ?? null);
  const [delimiters, setDelimiters] = useState<LoadFileDelimiters | null>(navState?.delimiters ?? null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);

  const [state, setState] = useState<WizardState>({
    filenameColumnHeader: null,
    matchSettings: { stripIncomingExtensions: false, stripCurrentExtensions: true },
    columnMapping: [],
  });

  const { mutateAsync: parseHeaderSample, isLoading: isParsing } = useParseLoadFileHeaderSample();
  const { mutateAsync: startImport, isLoading: isStarting } = useStartLoadFileImport();
  const { data: importSummary, isFetching: isFetchingSummary } = useGetLoadFileImport(matterId, importId);

  // On deep-link/refresh (no nav state), re-parse the file to rebuild the preview.
  useEffect(() => {
    if (parseResult || !matterId || !importId) return;

    // Wait for the summary query to settle before deciding. When navigating back to a
    // now-completed import, React Query serves the stale pre-run status (e.g. "mapping")
    // from cache while it refetches; acting on that would re-parse a completed import and
    // 409 ("cannot be parsed from status completed"). Only decide on freshly fetched data.
    if (isFetchingSummary) return;

    // Once an import has been queued/run the wizard shows the progress step, which
    // doesn't need the preview — and re-parsing would clobber the import's persisted
    // status. Wait for the summary, then skip the re-parse for those imports.
    if (!importSummary || RUNNING_OR_DONE_STATUSES.includes(importSummary.status)) return;

    parseHeaderSample({ matterId, importId })
      .then((result) => {
        setParseResult(result);
        setDelimiters(result.detectedDelimiters);
        setParseError(null);
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'Failed to load file preview';
        setParseError(message);
        toast.error(message);
      });
  }, [parseResult, matterId, importId, parseHeaderSample, importSummary, isFetchingSummary]);

  // Initialise the column mapping (all columns present, none mapped yet) once parsed.
  useEffect(() => {
    if (!parseResult || state.columnMapping.length > 0) return;
    const multiValue = delimiters?.multiValue ?? ';';
    setState((prev) => ({
      ...prev,
      columnMapping: parseResult.headers.map((header, i) => ({
        ...defaultColumn(header, sampleValuesFor(parseResult, i), multiValue),
        enabled: false, // the right-hand "mapped" table starts empty
      })),
    }));
  }, [parseResult, state.columnMapping.length, delimiters?.multiValue]);

  // If the import already ran (deep-link into a completed/processing import), jump to progress.
  useEffect(() => {
    if (!importSummary || !RUNNING_OR_DONE_STATUSES.includes(importSummary.status)) return;
    // Failed imports are terminal dead-ends — send deep links / stale URLs back to
    // the imports list rather than into an empty progress view.
    if (importSummary.status === 'failed') {
      navigate('../load-file-import', { replace: true });
      return;
    }

    setJobId(importSummary.jobId);
    setStepIndex(3);
  }, [importSummary, navigate]);

  /**
   * Monotonic sequence for delimiter re-parses; a response is applied only if it
   * is still the latest request, so a slow early parse can't clobber a later one.
   */
  const parseSeqRef = useRef(0);

  const handleDelimitersChange = (partial: Partial<LoadFileDelimiters>) => {
    if (!matterId || !importId || !delimiters) return;
    const next = { ...delimiters, ...partial };
    setDelimiters(next);

    // Newline/multi-value are post-parse settings: the header preview is
    // unaffected, so skip the round trip. A multi-value change still clears the
    // column mapping so the init effect re-seeds the array defaults with it.
    const affectsParsing = ['encoding', 'field', 'quote'].some((key) => key in partial);
    if (!affectsParsing) {
      if ('multiValue' in partial) {
        setState((prev) => ({ ...prev, columnMapping: [] }));
      }
      return;
    }

    const seq = ++parseSeqRef.current;
    parseHeaderSample({ matterId, importId, delimiters: next })
      .then((result) => {
        if (seq !== parseSeqRef.current) return; // stale response
        setParseResult(result);
        // Downstream selections were made against the old headers; reset them
        // so the init effects re-seed from the fresh parse.
        setState((prev) => ({ ...prev, filenameColumnHeader: null, columnMapping: [] }));
      })
      .catch((error: unknown) => {
        if (seq !== parseSeqRef.current) return;
        // Keep the previous headers on a failed re-parse; the user can adjust again.
        toast.error(error instanceof Error ? error.message : 'Failed to re-parse the load file');
      });
  };

  // One value per column: the first non-empty cell across the sample rows.
  const sampleValues = useMemo(
    () =>
      parseResult
        ? parseResult.headers.map((_, i) => sampleValuesFor(parseResult, i).find((v) => v.trim() !== '') ?? '')
        : [],
    [parseResult],
  );
  const enabledCount = state.columnMapping.filter((c) => c.enabled).length;

  const patch = (partial: Partial<WizardState>) => setState((prev) => ({ ...prev, ...partial }));

  const handleStart = async () => {
    if (!matterId || !importId || !parseResult || !delimiters) return;
    if (!state.filenameColumnHeader) {
      toast.error('Choose a filename identifier column first');
      return;
    }
    if (enabledCount === 0) {
      toast.error('Bring across at least one field to import');
      return;
    }
    try {
      const { jobId: newJobId } = await startImport({
        matterId,
        importId,
        filenameColumnHeader: state.filenameColumnHeader,
        matchSettings: state.matchSettings,
        // Disabled columns aren't imported; sending only enabled ones keeps junk
        // columns from a malformed file (blank/mangled headers) out of the request.
        columnMapping: state.columnMapping.filter((col) => col.enabled),
        delimiters,
      });
      trackCustomEvent(TrackingEventsV2.RelativityLoadFileImport, {
        matterId,
        importId,
        mappedColumns: enabledCount,
      });
      setJobId(newJobId);
      setStepIndex(3);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start import');
    }
  };

  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));
  // Steps are clickable only to navigate back to an already-visited step, and never once the import has started.
  const canGoToStep = (i: number) => i < stepIndex && stepIndex < 3;
  const goToStep = (i: number) => {
    if (canGoToStep(i)) setStepIndex(i);
  };
  const goNext = () => {
    if (stepIndex === 0) {
      if (!parseResult || isParsing) return;
      setStepIndex(1);
    } else if (stepIndex === 1) {
      if (!state.filenameColumnHeader) {
        toast.error('Choose a filename identifier column first');
        return;
      }
      setStepIndex(2);
    } else if (stepIndex === 2) {
      void handleStart();
    }
  };

  if (!matterId || !importId) return null;

  const isLoadingPreview = !parseResult && isParsing;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <SettingsBackLink to="../load-file-import" label="Back to imports" />

      {/* Full-width step header: all step names shown at the top always, matter-creation style */}
      <div className="-mx-8 mt-2 mb-6 shrink-0 border-b px-8 pr-12 pb-5">
        <div className="flex items-center justify-between gap-5">
          <div className="flex items-center justify-start">
            {STEPS.map((step, i) => (
              <span key={step.label} className="flex items-center">
                {i > 0 && <Icon className="lucide--arrow-right mx-4 mt-px text-gray-300" />}
                <button
                  type="button"
                  disabled={!canGoToStep(i)}
                  onClick={() => goToStep(i)}
                  className={cn(
                    'py-1.5 font-serif text-[22px] duration-150',
                    i === stepIndex ? 'text-gray-900' : 'text-gray-300',
                    canGoToStep(i) ? 'cursor-pointer hover:text-gray-400' : 'cursor-default',
                  )}
                >
                  {step.label}
                </button>
              </span>
            ))}
          </div>

          <div className="flex shrink-0 gap-2.5">
            {STEPS.map((step, i) => (
              <div
                key={step.label}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-500 ease-in-out',
                  i === stepIndex ? 'w-6 bg-teal-400' : i < stepIndex ? 'w-1.5 bg-teal-500/60' : 'w-1.5 bg-gray-300',
                )}
              />
            ))}
          </div>
        </div>

        <p className="mt-1 max-w-3xl text-sm text-gray-500">{STEPS[stepIndex].description}</p>
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col">
        {parseError && !parseResult && !isParsing ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{parseError}</div>
            <p className="text-sm text-gray-400">Fix the file and upload it again from the imports page.</p>
          </div>
        ) : isLoadingPreview ? (
          <div className="flex flex-1 items-center justify-center gap-2 py-16 text-sm text-gray-400">
            <Icon className="lucide--loader animate-spin ease-in-out" />
            Loading file preview...
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-auto p-1">
            {stepIndex === 0 && parseResult && delimiters && (
              <LoadFileSettingsStep
                parseResult={parseResult}
                delimiters={delimiters}
                isParsing={isParsing}
                onDelimitersChange={handleDelimitersChange}
              />
            )}

            {stepIndex === 1 && parseResult && (
              <FilenameStep
                matterId={matterId}
                parseResult={parseResult}
                fileName={importSummary?.fileName}
                filenameColumnHeader={state.filenameColumnHeader}
                matchSettings={state.matchSettings}
                onChange={patch}
              />
            )}

            {stepIndex === 2 && parseResult && (
              <MappingStep
                columnMapping={state.columnMapping}
                sampleValues={sampleValues}
                multiValueDelimiter={delimiters?.multiValue ?? ';'}
                onChange={(columnMapping) => patch({ columnMapping })}
              />
            )}

            {stepIndex === 3 && <ProgressStep matterId={matterId} importId={importId} jobId={jobId} />}
          </div>
        )}

        {/* Navigation */}
        {stepIndex === 0 && !isLoadingPreview && parseResult && (
          <div className="flex shrink-0 items-center justify-end pt-6">
            <Button variant="primary" onClick={goNext} disabled={isParsing}>
              Next
              <Icon className="lucide--arrow-right" />
            </Button>
          </div>
        )}

        {stepIndex === 1 && !isLoadingPreview && parseResult && (
          <div className="flex shrink-0 items-center justify-between pt-6">
            <Button variant="ghost" onClick={goBack}>
              <Icon className="lucide--arrow-left" />
              Back
            </Button>
            <Button variant="primary" onClick={goNext} disabled={!state.filenameColumnHeader}>
              Next
              <Icon className="lucide--arrow-right" />
            </Button>
          </div>
        )}

        {stepIndex === 2 && !isLoadingPreview && (
          <div className="flex shrink-0 items-center justify-between pt-6">
            <Button variant="ghost" onClick={goBack}>
              <Icon className="lucide--arrow-left" />
              Back
            </Button>
            <Button
              variant="primary"
              onClick={goNext}
              disabled={isStarting || !state.columnMapping.some((col) => col.enabled)}
            >
              {isStarting ? (
                <>
                  <Icon className="lucide--loader animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  Start import
                  <Icon className="lucide--arrow-right" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
